import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { bookingApi, businessApi } from '../services/api';
import { Business } from '../types';
import axios from 'axios';

const API_URL = (import.meta as any).env?.VITE_API_URL || 'https://fitpulse.es/api';

const STATUS_LABELS: Record<string, string> = {
  CONFIRMED: 'Confirmada', COMPLETED: 'Completada',
  PENDING: 'Pendiente', CANCELLED: 'Cancelada', NO_SHOW: 'No asistió',
};
const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  CONFIRMED: { bg: '#dcfce7', text: '#15803d' },
  COMPLETED: { bg: '#dbeafe', text: '#1d4ed8' },
  PENDING: { bg: '#fef9c3', text: '#854d0e' },
  CANCELLED: { bg: '#fee2e2', text: '#dc2626' },
  NO_SHOW: { bg: '#f1f5f9', text: '#475569' },
};

function fmt(d: string) {
  return new Date(d).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Europe/Madrid' });
}
function fmtT(d: string) {
  return new Date(d).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Madrid' });
}

export function Bookings() {
  const [bookings, setBookings] = useState<any[]>([]);
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [selectedBusiness, setSelectedBusiness] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState<any | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const bRes = await businessApi.getMyBusinesses();
      const bList = bRes.data.data.businesses || [];
      setBusinesses(bList);
      if (bList.length > 0) {
        await loadBookings(bList[0].id);
        setSelectedBusiness(bList[0].id);
      }
    } catch { toast.error('Error cargando reservas'); }
    finally { setIsLoading(false); }
  };

  const loadBookings = async (businessId: string, status?: string) => {
    try {
      const token = localStorage.getItem('accessToken');
      let url = `${API_URL}/bookings/business/${businessId}?limit=100`;
      if (status) url += `&status=${status}`;
      const r = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setBookings(r.data.data.bookings || []);
    } catch { toast.error('Error cargando reservas'); }
  };

  const handleBusinessChange = async (id: string) => {
    setSelectedBusiness(id);
    setSelectedStatus('');
    await loadBookings(id);
  };

  const handleStatusFilter = async (status: string) => {
    setSelectedStatus(status);
    await loadBookings(selectedBusiness, status || undefined);
  };

  const handleStatusUpdate = async (bookingId: string, status: string) => {
    setUpdatingStatus(true);
    try {
      await bookingApi.updateBooking(bookingId, { status } as any);
      toast.success('Estado actualizado');
      setSelectedBooking((prev: any) => prev ? { ...prev, status } : null);
      await loadBookings(selectedBusiness, selectedStatus || undefined);
    } catch { toast.error('Error al actualizar'); }
    finally { setUpdatingStatus(false); }
  };

  const filtered = bookings.filter(b => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      b.client?.firstName?.toLowerCase().includes(q) ||
      b.client?.lastName?.toLowerCase().includes(q) ||
      b.client?.email?.toLowerCase().includes(q) ||
      b.event?.title?.toLowerCase().includes(q) ||
      b.service?.name?.toLowerCase().includes(q)
    );
  });

  const stats = {
    total: bookings.length,
    confirmed: bookings.filter(b => b.status === 'CONFIRMED').length,
    pending: bookings.filter(b => b.status === 'PENDING').length,
    cancelled: bookings.filter(b => b.status === 'CANCELLED').length,
  };

  if (isLoading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
    </div>
  );

  return (
    <div className="space-y-6">
      <style>{`
        .booking-row { transition: background .15s; cursor: pointer; }
        .booking-row:hover { background: #f8fafc; }
        .stat-mini { background: white; border: 1px solid #e2e8f0; border-radius: 12px; padding: 16px 20px; }
        .modal-bg { position: fixed; inset: 0; background: rgba(0,0,0,.5); z-index: 100; display: flex; align-items: center; justify-content: center; padding: 20px; backdrop-filter: blur(4px); }
        .modal-box { background: white; border-radius: 20px; padding: 32px; max-width: 500px; width: 100%; box-shadow: 0 40px 100px rgba(0,0,0,.2); max-height: 90vh; overflow-y: auto; }
      `}</style>

      {/* HEADER */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Reservas</h1>
          <p className="text-secondary-500 text-sm mt-1">Gestiona las inscripciones y reservas de tus clientes</p>
        </div>
        <select className="input max-w-xs" value={selectedBusiness} onChange={e => handleBusinessChange(e.target.value)}>
          {businesses.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
      </div>

      {/* STATS */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="stat-mini">
          <p className="text-xs font-semibold text-secondary-400 uppercase mb-1">Total</p>
          <p className="text-2xl font-bold text-secondary-900">{stats.total}</p>
        </div>
        <div className="stat-mini">
          <p className="text-xs font-semibold text-secondary-400 uppercase mb-1">Confirmadas</p>
          <p className="text-2xl font-bold text-green-600">{stats.confirmed}</p>
        </div>
        <div className="stat-mini">
          <p className="text-xs font-semibold text-secondary-400 uppercase mb-1">Pendientes</p>
          <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
        </div>
        <div className="stat-mini">
          <p className="text-xs font-semibold text-secondary-400 uppercase mb-1">Canceladas</p>
          <p className="text-2xl font-bold text-red-500">{stats.cancelled}</p>
        </div>
      </div>

      {/* FILTROS */}
      <div className="card p-4 flex flex-wrap gap-3 items-center">
        <input
          type="text" placeholder="🔍 Buscar cliente, evento, servicio..."
          className="input flex-1 min-w-48" style={{ maxWidth: 320 }}
          value={search} onChange={e => setSearch(e.target.value)}
        />
        <div className="flex gap-2 flex-wrap">
          {['', 'CONFIRMED', 'PENDING', 'CANCELLED', 'COMPLETED', 'NO_SHOW'].map(s => (
            <button key={s}
              onClick={() => handleStatusFilter(s)}
              style={{
                padding: '6px 14px', borderRadius: 100, border: '1px solid',
                fontSize: '.78rem', fontWeight: 600, cursor: 'pointer', transition: 'all .2s',
                background: selectedStatus === s ? '#3b82f6' : 'white',
                color: selectedStatus === s ? 'white' : '#64748b',
                borderColor: selectedStatus === s ? '#3b82f6' : '#e2e8f0',
              }}>
              {s ? STATUS_LABELS[s] : 'Todas'}
            </button>
          ))}
        </div>
      </div>

      {/* TABLA */}
      {filtered.length > 0 ? (
        <div className="card overflow-hidden" style={{ padding: 0 }}>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                <tr>
                  {['Cliente', 'Evento / Servicio', 'Fecha', 'Importe', 'Estado', ''].map(h => (
                    <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: '.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.05em', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(b => {
                  const sc = STATUS_COLORS[b.status] || STATUS_COLORS.PENDING;
                  return (
                    <tr key={b.id} className="booking-row" style={{ borderBottom: '1px solid #f1f5f9' }} onClick={() => setSelectedBooking(b)}>
                      <td style={{ padding: '14px 16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 700, fontSize: '.85rem', flexShrink: 0 }}>
                            {b.client?.firstName?.charAt(0)}{b.client?.lastName?.charAt(0)}
                          </div>
                          <div>
                            <p style={{ fontSize: '.88rem', fontWeight: 600, color: '#1e293b' }}>{b.client?.firstName} {b.client?.lastName}</p>
                            <p style={{ fontSize: '.75rem', color: '#94a3b8' }}>{b.client?.email}</p>
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        <p style={{ fontSize: '.88rem', fontWeight: 600, color: '#1e293b' }}>{b.event?.title || b.service?.name || '—'}</p>
                        {b.service && <p style={{ fontSize: '.75rem', color: '#94a3b8' }}>🎯 {b.service.name}</p>}
                      </td>
                      <td style={{ padding: '14px 16px', fontSize: '.82rem', color: '#475569', whiteSpace: 'nowrap' }}>
                        {b.event?.startTime ? (
                          <>
                            <p>{fmt(b.event.startTime)}</p>
                            <p style={{ color: '#94a3b8' }}>🕐 {fmtT(b.event.startTime)}</p>
                          </>
                        ) : <p>{fmt(b.createdAt)}</p>}
                      </td>
                      <td style={{ padding: '14px 16px', fontSize: '.88rem', fontWeight: 700, color: '#1e293b', whiteSpace: 'nowrap' }}>
                        {b.amount ? `${b.amount}€` : b.service?.price ? `${b.service.price}€` : '—'}
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        <span style={{ background: sc.bg, color: sc.text, fontSize: '.72rem', fontWeight: 700, padding: '4px 10px', borderRadius: 100 }}>
                          {STATUS_LABELS[b.status] || b.status}
                        </span>
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        <span style={{ fontSize: '.78rem', color: '#3b82f6', fontWeight: 600 }}>Ver →</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="card p-12 text-center">
          <div style={{ fontSize: '3rem', marginBottom: 12 }}>📋</div>
          <p className="text-secondary-500 font-medium">No hay reservas</p>
          <p className="text-secondary-400 text-sm mt-1">Las inscripciones aparecerán aquí cuando alguien se apunte a un evento</p>
        </div>
      )}

      {/* MODAL DETALLE */}
      {selectedBooking && (
        <div className="modal-bg" onClick={() => setSelectedBooking(null)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
              <h2 style={{ fontSize: '1.2rem', fontWeight: 800, color: '#1e293b' }}>Detalle de reserva</h2>
              <button onClick={() => setSelectedBooking(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '1.2rem' }}>✕</button>
            </div>

            {/* Cliente */}
            <div style={{ background: '#f8fafc', borderRadius: 14, padding: '20px', marginBottom: 20 }}>
              <p style={{ fontSize: '.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 12 }}>Cliente</p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 700, fontSize: '1.1rem', flexShrink: 0 }}>
                  {selectedBooking.client?.firstName?.charAt(0)}{selectedBooking.client?.lastName?.charAt(0)}
                </div>
                <div>
                  <p style={{ fontWeight: 700, fontSize: '1rem', color: '#1e293b' }}>{selectedBooking.client?.firstName} {selectedBooking.client?.lastName}</p>
                  <p style={{ fontSize: '.82rem', color: '#64748b' }}>✉️ {selectedBooking.client?.email}</p>
                  {selectedBooking.client?.phone && <p style={{ fontSize: '.82rem', color: '#64748b' }}>📞 {selectedBooking.client?.phone}</p>}
                </div>
              </div>
            </div>

            {/* Evento/Servicio */}
            <div style={{ background: '#f8fafc', borderRadius: 14, padding: '20px', marginBottom: 20 }}>
              <p style={{ fontSize: '.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 12 }}>Reserva</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {selectedBooking.event && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.88rem' }}>
                    <span style={{ color: '#64748b' }}>Evento</span>
                    <span style={{ fontWeight: 600, color: '#1e293b' }}>{selectedBooking.event.title}</span>
                  </div>
                )}
                {selectedBooking.service && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.88rem' }}>
                    <span style={{ color: '#64748b' }}>Servicio</span>
                    <span style={{ fontWeight: 600, color: '#1e293b' }}>{selectedBooking.service.name}</span>
                  </div>
                )}
                {selectedBooking.event?.startTime && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.88rem' }}>
                    <span style={{ color: '#64748b' }}>Fecha</span>
                    <span style={{ fontWeight: 600, color: '#1e293b' }}>{fmt(selectedBooking.event.startTime)} · {fmtT(selectedBooking.event.startTime)}</span>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.88rem' }}>
                  <span style={{ color: '#64748b' }}>Importe</span>
                  <span style={{ fontWeight: 700, color: '#1e293b' }}>
                    {selectedBooking.amount ? `${selectedBooking.amount}€` : selectedBooking.service?.price ? `${selectedBooking.service.price}€` : '—'}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.88rem' }}>
                  <span style={{ color: '#64748b' }}>Inscripción</span>
                  <span style={{ color: '#94a3b8' }}>{fmt(selectedBooking.createdAt)}</span>
                </div>
              </div>
            </div>

            {/* Estado */}
            <div style={{ marginBottom: 24 }}>
              <p style={{ fontSize: '.82rem', fontWeight: 600, color: '#475569', marginBottom: 8 }}>Cambiar estado</p>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {['CONFIRMED', 'PENDING', 'COMPLETED', 'CANCELLED', 'NO_SHOW'].map(s => {
                  const sc = STATUS_COLORS[s];
                  const isActive = selectedBooking.status === s;
                  return (
                    <button key={s}
                      onClick={() => handleStatusUpdate(selectedBooking.id, s)}
                      disabled={updatingStatus || isActive}
                      style={{
                        padding: '8px 14px', borderRadius: 10, border: '1px solid',
                        fontSize: '.78rem', fontWeight: 700, cursor: isActive ? 'default' : 'pointer',
                        background: isActive ? sc.bg : 'white',
                        color: isActive ? sc.text : '#64748b',
                        borderColor: isActive ? sc.text : '#e2e8f0',
                        opacity: updatingStatus ? .6 : 1,
                        transition: 'all .2s',
                      }}>
                      {STATUS_LABELS[s]}
                    </button>
                  );
                })}
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              {selectedBooking.client?.phone && (
                <a href={`https://wa.me/${selectedBooking.client.phone.replace(/\D/g,'')}`} target="_blank" rel="noreferrer"
                  style={{ flex: 1, padding: '11px', borderRadius: 10, background: '#dcfce7', color: '#15803d', border: 'none', fontWeight: 700, fontSize: '.85rem', textAlign: 'center', textDecoration: 'none', display: 'block' }}>
                  💬 WhatsApp
                </a>
              )}
              {selectedBooking.client?.email && (
                <a href={`mailto:${selectedBooking.client.email}`}
                  style={{ flex: 1, padding: '11px', borderRadius: 10, background: '#dbeafe', color: '#1d4ed8', border: 'none', fontWeight: 700, fontSize: '.85rem', textAlign: 'center', textDecoration: 'none', display: 'block' }}>
                  ✉️ Email
                </a>
              )}
              <button onClick={() => setSelectedBooking(null)}
                style={{ flex: 1, padding: '11px', borderRadius: 10, background: '#f1f5f9', color: '#475569', border: 'none', fontWeight: 700, fontSize: '.85rem', cursor: 'pointer' }}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
